/**
 * Centralized logging utility for CondoShield CRM
 * - Removes console.log statements in production builds
 * - Maintains error logging for monitoring
 * - Provides structured logging with context
 */

const isDevelopment = import.meta.env.DEV;
const isTest = import.meta.env.MODE === 'test';

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

// Current log level (can be configured via environment)
const CURRENT_LOG_LEVEL = isDevelopment
  ? LOG_LEVELS.DEBUG
  : LOG_LEVELS.ERROR; // Only errors in production

/**
 * Format log message with timestamp and context
 */
function formatMessage(level, context, message, ...args) {
  const timestamp = new Date().toISOString();
  const prefix = context ? `[${context}]` : '';
  return [`${timestamp} ${level} ${prefix}`, message, ...args];
}

/**
 * Should this log level be displayed?
 */
function shouldLog(level) {
  return LOG_LEVELS[level] >= CURRENT_LOG_LEVEL;
}

/**
 * Logger class with context support
 */
class Logger {
  constructor(context = '') {
    this.context = context;
  }

  /**
   * Debug logging - development only
   * Use for detailed debugging information
   */
  debug(message, ...args) {
    if (shouldLog('DEBUG')) {
      console.log(...formatMessage('🐛 DEBUG', this.context, message, ...args));
    }
  }

  /**
   * Info logging - development only
   * Use for general information
   */
  info(message, ...args) {
    if (shouldLog('INFO')) {
      console.info(...formatMessage('ℹ️  INFO', this.context, message, ...args));
    }
  }

  /**
   * Warning logging - all environments
   * Use for recoverable errors or deprecation warnings
   */
  warn(message, ...args) {
    if (shouldLog('WARN')) {
      console.warn(...formatMessage('⚠️  WARN', this.context, message, ...args));
    }
  }

  /**
   * Error logging - all environments
   * Use for errors that should be monitored
   */
  error(message, ...args) {
    if (shouldLog('ERROR')) {
      console.error(...formatMessage('❌ ERROR', this.context, message, ...args));

      // TODO: Integrate with error monitoring service (Sentry, etc.)
      // sendToErrorMonitoring({ level: 'error', message, context: this.context, args });
    }
  }

  /**
   * Performance logging - development only
   * Use for timing operations
   */
  time(label) {
    if (isDevelopment && !isTest) {
      console.time(`[${this.context}] ${label}`);
    }
  }

  timeEnd(label) {
    if (isDevelopment && !isTest) {
      console.timeEnd(`[${this.context}] ${label}`);
    }
  }

  /**
   * Table logging - development only
   * Use for displaying data in table format
   */
  table(data) {
    if (isDevelopment && !isTest) {
      console.table(data);
    }
  }

  /**
   * Group logging - development only
   * Use for collapsing related logs
   */
  group(label) {
    if (isDevelopment && !isTest) {
      console.group(`[${this.context}] ${label}`);
    }
  }

  groupEnd() {
    if (isDevelopment && !isTest) {
      console.groupEnd();
    }
  }

  /**
   * Create a child logger with additional context
   */
  child(subContext) {
    const newContext = this.context ? `${this.context}:${subContext}` : subContext;
    return new Logger(newContext);
  }
}

// Default logger instance
const defaultLogger = new Logger();

/**
 * Create a logger with specific context
 * @param {string} context - Context identifier (e.g., component name, module name)
 * @returns {Logger} Logger instance with context
 */
export function createLogger(context) {
  return new Logger(context);
}

// Export default logger methods
export const logger = {
  debug: (...args) => defaultLogger.debug(...args),
  info: (...args) => defaultLogger.info(...args),
  warn: (...args) => defaultLogger.warn(...args),
  error: (...args) => defaultLogger.error(...args),
  time: label => defaultLogger.time(label),
  timeEnd: label => defaultLogger.timeEnd(label),
  table: data => defaultLogger.table(data),
  group: label => defaultLogger.group(label),
  groupEnd: () => defaultLogger.groupEnd(),
  createLogger,
};

// Export as default for convenience
export default logger;

// Example usage:
// import logger from './utils/logger';
// import { createLogger } from './utils/logger';
//
// logger.debug('App initialized');
// logger.info('User logged in', { userId: 123 });
// logger.warn('API rate limit approaching');
// logger.error('Failed to save data', error);
//
// const componentLogger = createLogger('AssociationDetails');
// componentLogger.debug('Rendering association', association.id);
// componentLogger.time('Data fetch');
// // ... operation ...
// componentLogger.timeEnd('Data fetch');
